import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import Downloader from "./wrappers/Downloader";
import logo from "./images/pdlogo.png";

import { SketchPicker } from 'react-color';
import { Box, Button, Grid, Stack, Typography, TextField } from "@mui/material";

import Patterns from "./Patterns";
import PatternMenu from "./controls/PatternMenu";
import Slider from "./controls/Slider";
import Switch from "./controls/Switch";
import SelectMenu from "./controls/SelectMenu";
import NumberInput from "./controls/NumberInput";
import CopyURLButton from "./controls/CopyURLButton";
import CopyLockedURLButton from "./controls/CopyLockedURLButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";

import Popover from "@mui/material/Popover";

import ScaleToWidth from "./wrappers/ScaleToWidth";

export default function App({ fullscreen = false }) {
   const { pattern: urlPattern } = useParams();
   const [searchParams, setSearchParams] = useSearchParams();
   const navigate = useNavigate();

   // Initialize state from URL or defaults
   const getParam = (key, defaultValue, parseFn = (v) => v) => {
      const value = searchParams.get(key);
      return value !== null ? parseFn(value) : defaultValue;
   };

   const [testPattern, setTestPattern] = React.useState(urlPattern || "");
   
   // Sync pattern from URL when it changes (e.g., browser back/forward)
   useEffect(() => {
      if (urlPattern !== testPattern) {
         setTestPattern(urlPattern || "");
      }
   }, [urlPattern]);

   const [circles, setCircles] = useState(getParam('circles', false, v => v === 'true'));
   const [text, setText] = useState(getParam('text', false, v => v === 'true'));
   const [luma, setLuma] = useState(getParam('luma', false, v => v === 'true'));
   const [grid, setGrid] = useState(getParam('grid', false, v => v === 'true'));

   const [gridSize, setGridSize] = React.useState(getParam('gridSize', 1, Number));
   const [bkgndColor, setBkgndColor] = React.useState(getParam('bkgndColor', '#333333'));

   const [resolution, setResolution] = React.useState(getParam('resolution', "1920x1080"));
   const [colorPickerOpen, setColorPickerOpen] = React.useState(false);
   const [colorPickerAnchor, setColorPickerAnchor] = React.useState(null);

   // Custom Letterbox state
   const [letterboxWidth, setLetterboxWidth] = React.useState(getParam('letterboxWidth', 1920, Number));
   const [letterboxHeight, setLetterboxHeight] = React.useState(getParam('letterboxHeight', 1080, Number));
   const [letterboxAlignVertical, setLetterboxAlignVertical] = React.useState(getParam('letterboxAlignVertical', "center"));
   const [letterboxAlignHorizontal, setLetterboxAlignHorizontal] = React.useState(getParam('letterboxAlignHorizontal', "center"));
   const [letterboxOutputResolution, setLetterboxOutputResolution] = React.useState(getParam('letterboxOutputResolution', "1920x1080"));

   // Audio Tone state
   const [audioFrequency, setAudioFrequency] = React.useState(getParam('audioFrequency', 1000, Number));
   const [audioVolume, setAudioVolume] = React.useState(getParam('audioVolume', -10, Number));

   // Moving Gradient state
   const [gradientColor1, setGradientColor1] = React.useState(getParam('gradientColor1', '#ff0000'));
   const [gradientColor2, setGradientColor2] = React.useState(getParam('gradientColor2', '#0000ff'));
   const [gradientColorPicker1Open, setGradientColorPicker1Open] = React.useState(false);
   const [gradientColorPicker2Open, setGradientColorPicker2Open] = React.useState(false);
   const [gradientColorPicker1Anchor, setGradientColorPicker1Anchor] = React.useState(null);
   const [gradientColorPicker2Anchor, setGradientColorPicker2Anchor] = React.useState(null);

   const patternRef = useRef(null);

   // Sync pattern from URL on mount and when URL changes
   useEffect(() => {
      if (urlPattern && urlPattern !== testPattern) {
         setTestPattern(urlPattern);
      }
   }, [urlPattern]);

   // Sync URL params when state changes
   const updateSearchParams = (updates) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
         if (value === null || value === undefined || value === '' || value === false) {
            newParams.delete(key);
         } else {
            newParams.set(key, String(value));
         }
      });
      setSearchParams(newParams, { replace: true });
   };

   // Wrapped setters that update URL
   const setTestPatternWithURL = (pattern) => {
      setTestPattern(pattern);
      if (pattern) {
         navigate(`/${pattern}`, { replace: true });
      } else {
         navigate('/', { replace: true });
      }
   };

   const setCirclesWithURL = (value) => {
      setCircles(value);
      updateSearchParams({ circles: value });
   };

   const setTextWithURL = (value) => {
      setText(value);
      updateSearchParams({ text: value });
   };

   const setLumaWithURL = (value) => {
      setLuma(value);
      updateSearchParams({ luma: value });
   };

   const setGridWithURL = (value) => {
      setGrid(value);
      updateSearchParams({ grid: value });
   };

   const setGridSizeWithURL = (value) => {
      setGridSize(value);
      updateSearchParams({ gridSize: value });
   };

   const setBkgndColorWithURL = (value) => {
      setBkgndColor(value);
      updateSearchParams({ bkgndColor: value });
   };

   const setResolutionWithURL = (value) => {
      setResolution(value);
      updateSearchParams({ resolution: value });
   };

   const setLetterboxWidthWithURL = (value) => {
      setLetterboxWidth(value);
      updateSearchParams({ letterboxWidth: value });
   };

   const setLetterboxHeightWithURL = (value) => {
      setLetterboxHeight(value);
      updateSearchParams({ letterboxHeight: value });
   };

   const setLetterboxAlignVerticalWithURL = (value) => {
      setLetterboxAlignVertical(value);
      updateSearchParams({ letterboxAlignVertical: value });
   };

   const setLetterboxAlignHorizontalWithURL = (value) => {
      setLetterboxAlignHorizontal(value);
      updateSearchParams({ letterboxAlignHorizontal: value });
   };

   const setLetterboxOutputResolutionWithURL = (value) => {
      setLetterboxOutputResolution(value);
      updateSearchParams({ letterboxOutputResolution: value });
   };

   const setAudioFrequencyWithURL = (value) => {
      setAudioFrequency(value);
      updateSearchParams({ audioFrequency: value });
   };

   const setAudioVolumeWithURL = (value) => {
      setAudioVolume(value);
      updateSearchParams({ audioVolume: value });
   };

   const setGradientColor1WithURL = (value) => {
      setGradientColor1(value);
      updateSearchParams({ gradientColor1: value });
   };

   const setGradientColor2WithURL = (value) => {
      setGradientColor2(value);
      updateSearchParams({ gradientColor2: value });
   };

   const handleColorChange = (newValue) => {
      setBkgndColorWithURL(newValue.hex);
    }

   const handleGradientColor1Change = (newValue) => {
      setGradientColor1WithURL(newValue.hex);
   }

   const handleGradientColor2Change = (newValue) => {
      setGradientColor2WithURL(newValue.hex);
   }

   const handleFullscreen = () => {
      // Calculate angle from lower left to upper right of 16:9 frame
      const angle = 180 - (Math.atan(9/16) * 180 / Math.PI); // ≈ 150.64 degrees
      
      // Create HTML content for the new window
      const htmlContent = `
         <!DOCTYPE html>
         <html>
         <head>
            <meta charset="utf-8">
            <title>Moving Gradient</title>
            <style>
               * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
               }
               body {
                  width: 100vw;
                  height: 100vh;
                  overflow: hidden;
                  background: linear-gradient(${angle}deg, ${gradientColor1} 0%, ${gradientColor2} 100%);
                  background-size: 200% 200%;
                  animation: moveGradient 5s ease-in-out infinite alternate;
               }
               @keyframes moveGradient {
                  0% {
                     background-position: 0% 0%;
                  }
                  100% {
                     background-position: 100% 100%;
                  }
               }
            </style>
         </head>
         <body></body>
         </html>
      `;
      
      // Open new window
      const newWindow = window.open('', '_blank', 'width=1920,height=1080');
      if (newWindow) {
         newWindow.document.write(htmlContent);
         newWindow.document.close();
         
         // Request fullscreen immediately after window opens
         const requestFullscreen = () => {
            const docEl = newWindow.document.documentElement;
            if (docEl.requestFullscreen) {
               docEl.requestFullscreen().catch(err => {
                  console.error(`Error attempting to enable fullscreen: ${err.message}`);
               });
            } else if (docEl.webkitRequestFullscreen) {
               docEl.webkitRequestFullscreen();
            } else if (docEl.msRequestFullscreen) {
               docEl.msRequestFullscreen();
            } else if (docEl.mozRequestFullScreen) {
               docEl.mozRequestFullScreen();
            }
         };
         
         // Try immediately, and also on load as fallback
         setTimeout(requestFullscreen, 100);
         newWindow.addEventListener('load', requestFullscreen);
      }
   }

   const handlePatternFullscreen = () => {
      // Build fullscreen URL with current pattern and all query params
      const fullscreenUrl = `/fullscreen/${testPattern}${window.location.search}`;
      const fullUrl = window.location.origin + fullscreenUrl;
      
      // Open new window with fullscreen URL
      const newWindow = window.open(fullUrl, '_blank', 'width=1920,height=1080');
      
      if (newWindow) {
         // Request fullscreen after window loads
         const requestFullscreen = () => {
            const docEl = newWindow.document.documentElement;
            if (docEl.requestFullscreen) {
               docEl.requestFullscreen().catch(err => {
                  console.error('Error attempting to enable fullscreen: ' + err.message);
               });
            } else if (docEl.webkitRequestFullscreen) {
               docEl.webkitRequestFullscreen();
            } else if (docEl.msRequestFullscreen) {
               docEl.msRequestFullscreen();
            } else if (docEl.mozRequestFullScreen) {
               docEl.mozRequestFullScreen();
            }
         };
         
         setTimeout(requestFullscreen, 1000);
         newWindow.addEventListener('load', requestFullscreen);
      }
   };

   const handleSnakeFullscreen = () => {
      const GRID_WIDTH = 32;
      const GRID_HEIGHT = 18;
      const GAP = 1;
      const PADDING = 1;
      
      const htmlContent = `
         <!DOCTYPE html>
         <html>
         <head>
            <meta charset="utf-8">
            <title>Snake Game</title>
            <style>
               * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
               }
               body {
                  width: 100vw;
                  height: 100vh;
                  overflow: hidden;
                  background: #000;
                  display: flex;
                  flex-direction: column;
                  align-items: stretch;
                  justify-content: stretch;
                  font-family: monospace;
               }
               #game-container {
                  width: 100vw;
                  height: 100vh;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  position: relative;
               }
               #grid-wrapper {
                  width: min(100vw, 100vh * 16 / 9);
                  height: min(100vh, 100vw * 9 / 16);
                  aspect-ratio: 16/9;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  position: relative;
                  z-index: 1;
               }
               #score {
                  position: absolute;
                  top: 20px;
                  left: 50%;
                  transform: translateX(-50%);
                  color: #fff;
                  font-size: 48px;
                  font-weight: bold;
                  z-index: 10000;
                  pointer-events: none;
                  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
               }
               #game-over {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  color: #ff0000;
                  font-size: 72px;
                  font-weight: bold;
                  z-index: 20000;
                  display: none;
                  pointer-events: none;
                  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
               }
               #grid {
                  display: grid;
                  grid-template-columns: repeat(${GRID_WIDTH}, 1fr);
                  grid-template-rows: repeat(${GRID_HEIGHT}, 1fr);
                  gap: ${GAP}px;
                  background-color: #111;
                  padding: ${PADDING}px;
                  width: 100%;
                  height: 100%;
                  aspect-ratio: 16/9;
                  border: 1px solid #ff0000;
                  max-width: 100%;
                  max-height: 100%;
               }
               .cell {
                  width: 100%;
                  height: 100%;
                  background-color: #222;
                  border-radius: 1px;
               }
               .cell.snake {
                  background-color: #00cc00;
                  border-radius: 2px;
               }
               .cell.head {
                  background-color: #00ff00;
                  border-radius: 2px;
                  border: 1px solid #00ffff;
               }
               .cell.food {
                  background-color: #ff0000;
                  border-radius: 50%;
               }
            </style>
         </head>
         <body>
            <div id="game-container">
               <div id="grid-wrapper">
                  <div id="score">Score: <span id="score-value">0</span></div>
                  <div id="game-over">GAME OVER</div>
                  <div id="grid"></div>
               </div>
            </div>
            <script>
               const GRID_WIDTH = ${GRID_WIDTH};
               const GRID_HEIGHT = ${GRID_HEIGHT};
               const DIRECTIONS = {
                  UP: { x: 0, y: -1 },
                  DOWN: { x: 0, y: 1 },
                  LEFT: { x: -1, y: 0 },
                  RIGHT: { x: 1, y: 0 }
               };
               
               let snake = [{ x: 16, y: 9 }];
               let food = { x: 20, y: 10 };
               let direction = DIRECTIONS.RIGHT;
               let gameOver = false;
               let score = 0;
               
               const gridEl = document.getElementById('grid');
               const scoreEl = document.getElementById('score-value');
               const gameOverEl = document.getElementById('game-over');
               
               function generateFood() {
                  return {
                     x: Math.floor(Math.random() * GRID_WIDTH),
                     y: Math.floor(Math.random() * GRID_HEIGHT)
                  };
               }
               
               function chooseDirection(head, currentDirection, foodPos, snakeBody) {
                  const directions = [
                     { dir: DIRECTIONS.UP, pos: { x: head.x, y: head.y - 1 } },
                     { dir: DIRECTIONS.DOWN, pos: { x: head.x, y: head.y + 1 } },
                     { dir: DIRECTIONS.LEFT, pos: { x: head.x - 1, y: head.y } },
                     { dir: DIRECTIONS.RIGHT, pos: { x: head.x + 1, y: head.y } }
                  ];
                  
                  const validDirections = directions.filter(({ dir, pos }) => {
                     if (dir.x === -currentDirection.x && dir.y === -currentDirection.y) return false;
                     if (pos.x < 0 || pos.x >= GRID_WIDTH || pos.y < 0 || pos.y >= GRID_HEIGHT) return false;
                     if (snakeBody.some(segment => segment.x === pos.x && segment.y === pos.y)) return false;
                     return true;
                  });
                  
                  if (validDirections.length === 0) return currentDirection;
                  
                  const distances = validDirections.map(({ dir, pos }) => {
                     const distance = Math.abs(pos.x - foodPos.x) + Math.abs(pos.y - foodPos.y);
                     const isStraight = dir.x === currentDirection.x && dir.y === currentDirection.y;
                     const isTowardsFood = 
                        (foodPos.x > head.x && dir.x > 0) ||
                        (foodPos.x < head.x && dir.x < 0) ||
                        (foodPos.y > head.y && dir.y > 0) ||
                        (foodPos.y < head.y && dir.y < 0);
                     return {
                        dir,
                        distance,
                        isStraight,
                        isTowardsFood,
                        score: distance - (isStraight ? 2 : 0) - (isTowardsFood ? 1 : 0)
                     };
                  });
                  
                  distances.sort((a, b) => a.score - b.score);
                  const bestStraight = distances.find(d => d.isStraight && d.score <= (distances[1] ? distances[1].score : Infinity));
                  if (bestStraight && Math.random() < 0.85) return bestStraight.dir;
                  if (Math.random() < 0.9) return distances[0].dir;
                  return distances.length > 1 ? distances[1].dir : distances[0].dir;
               }
               
               function render() {
                  gridEl.innerHTML = '';
                  for (let y = 0; y < GRID_HEIGHT; y++) {
                     for (let x = 0; x < GRID_WIDTH; x++) {
                        const cell = document.createElement('div');
                        cell.className = 'cell';
                        const isSnake = snake.some(s => s.x === x && s.y === y);
                        const isHead = snake[0] && snake[0].x === x && snake[0].y === y;
                        const isFood = food.x === x && food.y === y;
                        
                        if (isHead) cell.className += ' head';
                        else if (isSnake) cell.className += ' snake';
                        else if (isFood) cell.className += ' food';
                        
                        gridEl.appendChild(cell);
                     }
                  }
                  scoreEl.textContent = score;
                  gameOverEl.style.display = gameOver ? 'block' : 'none';
               }
               
               function gameLoop() {
                  if (gameOver) {
                     setTimeout(() => {
                        snake = [{ x: 16, y: 9 }];
                        food = generateFood();
                        direction = DIRECTIONS.RIGHT;
                        gameOver = false;
                        score = 0;
                     }, 2000);
                     return;
                  }
                  
                  const head = snake[0];
                  const newDir = chooseDirection(head, direction, food, snake);
                  direction = newDir;
                  
                  const newHead = {
                     x: head.x + newDir.x,
                     y: head.y + newDir.y
                  };
                  
                  if (newHead.x < 0 || newHead.x >= GRID_WIDTH || newHead.y < 0 || newHead.y >= GRID_HEIGHT) {
                     gameOver = true;
                     render();
                     return;
                  }
                  
                  if (snake.some(s => s.x === newHead.x && s.y === newHead.y)) {
                     gameOver = true;
                     render();
                     return;
                  }
                  
                  snake = [newHead, ...snake];
                  
                  if (newHead.x === food.x && newHead.y === food.y) {
                     score++;
                     food = generateFood();
                  } else {
                     snake.pop();
                  }
                  
                  render();
               }
               
               // Initialize grid
               render();
               
               // Start game loop
               setInterval(gameLoop, 40);
            </script>
         </body>
         </html>
      `;
      
      const newWindow = window.open('', '_blank', 'width=1920,height=1080');
      if (newWindow) {
         newWindow.document.write(htmlContent);
         newWindow.document.close();
         
         const requestFullscreen = () => {
            const docEl = newWindow.document.documentElement;
            if (docEl.requestFullscreen) {
               docEl.requestFullscreen().catch(err => {
                  console.error('Error attempting to enable fullscreen: ' + err.message);
               });
            } else if (docEl.webkitRequestFullscreen) {
               docEl.webkitRequestFullscreen();
            } else if (docEl.msRequestFullscreen) {
               docEl.msRequestFullscreen();
            } else if (docEl.mozRequestFullScreen) {
               docEl.mozRequestFullScreen();
            }
         };
         
         setTimeout(requestFullscreen, 100);
         newWindow.addEventListener('load', requestFullscreen);
      }
   }

   // Determine canvas dimensions for ScaleToWidth based on Output resolution
   const getCanvasDimensions = () => {
      if (testPattern === "customLetterbox" && letterboxOutputResolution) {
         const [width, height] = letterboxOutputResolution.split('x').map(Number);
         return { width, height };
      }
      // Use the resolution from Output selector
      if (resolution) {
         const [width, height] = resolution.split('x').map(Number);
         return { width, height };
      }
      return { width: 1920, height: 1080 };
   };

   const canvasDimensions = getCanvasDimensions();
   const currentResolution = testPattern === "customLetterbox" ? letterboxOutputResolution : resolution;

   // If in fullscreen mode, render only the pattern
   if (fullscreen) {
      return (
         <Box
            sx={{
               width: '100vw',
               height: '100vh',
               overflow: 'hidden',
               background: '#000',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
            }}
         >
            <Box
               data-pattern-container
               sx={{
                  width: '100vw',
                  aspectRatio: '16/9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#000',
               }}
            >
               <ScaleToWidth 
                  contentWidth={canvasDimensions.width} 
                  contentHeight={canvasDimensions.height}
                  noMaxWidth={testPattern === "grid" || testPattern === "colorBars" || testPattern === "colorBarsPD" || testPattern === "safety"}
               >
                  {(parent) => (
                     <Patterns
                        pattern={testPattern}
                        gridSize={gridSize}
                        circles={circles}
                        luma={luma}
                        text={text}
                        grid={grid}
                        bkgndColor={bkgndColor}
                        letterboxWidth={letterboxWidth}
                        letterboxHeight={letterboxHeight}
                        letterboxAlignVertical={letterboxAlignVertical}
                        letterboxAlignHorizontal={letterboxAlignHorizontal}
                        letterboxOutputResolution={letterboxOutputResolution}
                        audioFrequency={audioFrequency}
                        audioVolume={audioVolume}
                        onAudioFrequencyChange={setAudioFrequencyWithURL}
                        onAudioVolumeChange={setAudioVolumeWithURL}
                        gradientColor1={gradientColor1}
                        gradientColor2={gradientColor2}
                        resolution={currentResolution}
                        width={canvasDimensions.width}
                        height={canvasDimensions.height}
                     />
                  )}
               </ScaleToWidth>
            </Box>
         </Box>
      );
   }

   return (
      <Box sx={{ p: 4 }}>
         <Stack
            data-controls
            direction='row'
            spacing={4}
            justifyContent='flex-start'
            alignItems='center'
            sx={{
               mb: 2,
               p: 4,
               borderRadius: 1,
               backgroundColor: "rgba(255,255,255,.9)",
            }}
         >
            <img src={logo} width={120} />

            <Stack direction="column" spacing={1}>
               <PatternMenu pattern={testPattern} callback={setTestPatternWithURL} />
               {testPattern && (
                  <Box sx={{ width: 300 }}>
                     <SelectMenu
                        label='Output'
                        value={testPattern === "customLetterbox" ? letterboxOutputResolution : resolution}
                        callback={testPattern === "customLetterbox" ? setLetterboxOutputResolutionWithURL : setResolutionWithURL}
                        options={["1280x720","1920x1080","3840x2160"]}
                        size="small"
                     />
                  </Box>
               )}
            </Stack>

            {testPattern && <CopyURLButton />}
            {testPattern && testPattern !== "audioTone" && <CopyLockedURLButton pattern={testPattern} searchParams={searchParams} />}

            {testPattern && (
               <Downloader
                  enabled={true}
                  resolution={testPattern === "customLetterbox" ? letterboxOutputResolution : resolution}
                  onResolutionChange={testPattern === "customLetterbox" ? setLetterboxOutputResolutionWithURL : setResolutionWithURL}
                  downloadName={"TestPattern_" + testPattern}
               >
                  <Patterns
                     pattern={testPattern}
                     gridSize={gridSize}
                     circles={circles}
                     luma={luma}
                     text={text}
                     grid={grid}
                     bkgndColor={bkgndColor}
                     letterboxWidth={letterboxWidth}
                     letterboxHeight={letterboxHeight}
                     letterboxAlignVertical={letterboxAlignVertical}
                     letterboxAlignHorizontal={letterboxAlignHorizontal}
                     letterboxOutputResolution={letterboxOutputResolution}
                     audioFrequency={audioFrequency}
                     audioVolume={audioVolume}
                     onAudioFrequencyChange={setAudioFrequencyWithURL}
                     onAudioVolumeChange={setAudioVolumeWithURL}
                     gradientColor1={gradientColor1}
                     gradientColor2={gradientColor2}
                     resolution={testPattern === "customLetterbox" ? letterboxOutputResolution : resolution}
                     width={canvasDimensions.width}
                     height={canvasDimensions.height}
                  />
               </Downloader>
            )}

            {testPattern && testPattern !== "audioTone" && (
               <Button
                  variant="contained"
                  onClick={testPattern === "snake" ? handleSnakeFullscreen : testPattern === "movingGradient" ? handleFullscreen : handlePatternFullscreen}
                  sx={{
                     width: 200,
                     height: 50,
                     ml: 2
                  }}
               >
                  Fullscreen
               </Button>
            )}
         </Stack>

            {testPattern == "grid" && (
            <Stack
               data-controls
               direction='row'
               spacing={4}
               justifyContent='flex-start'
               alignItems='center'
               sx={{
                  mb: 2,
                  px: 4,
                  py: 2,
                  borderRadius: 1,
                  backgroundColor: "rgba(255,255,255,.7)",
               }}
            >
               <Switch label='Circles' value={circles} callback={setCirclesWithURL} />
               <Switch label='Text' value={text} callback={setTextWithURL} />

               <Stack direction='row' spacing={2} alignItems='center' pl={2}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Background</Typography>
                  <RadioGroup
                     sx={{
                        flexDirection: "column",
                        alignItems: "flex-start"
                     }}
                     value={grid ? "grid" : luma ? "luma" : "none"}
                     onChange={(e) => {
                        const val = e.target.value;
                        const newGrid = val === "grid";
                        const newLuma = val === "luma";
                        setGrid(newGrid);
                        setLuma(newLuma);
                        // Update both in a single URL update, remove if false
                        const updates = {};
                        if (newGrid) {
                           updates.grid = true;
                           updates.luma = false;
                        } else if (newLuma) {
                           updates.grid = false;
                           updates.luma = true;
                        } else {
                           // "none" - remove both from URL
                           updates.grid = null;
                           updates.luma = null;
                        }
                        updateSearchParams(updates);
                     }}
                     row
                  >
                     <FormControlLabel value="none" control={<Radio />} label="None" />
                     <FormControlLabel value="grid" control={<Radio />} label="Grid" />
                     <FormControlLabel value="luma" control={<Radio />} label="Luma" />
                  </RadioGroup>
               </Stack>

               {grid && (
                  <Slider label='Grid Size' value={gridSize} callback={setGridSizeWithURL} />
               )}
               
               {!luma && (
                  <Stack direction='row' spacing={2} alignItems='center' pl={2}>
                     <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Background</Typography>
                     <Popover
                     open={colorPickerOpen}
                     anchorEl={colorPickerAnchor}
                     onClose={() => setColorPickerOpen(false)}
                     anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                     }}
                  >
                     <SketchPicker color={bkgndColor} onChange={handleColorChange} disableAlpha={true} />
                  </Popover>
                  <Button onClick={(e) => {
                     setColorPickerAnchor(e.currentTarget);
                     setColorPickerOpen(true);
                  }}>
                     <Box sx={{ width: 30, height: 30, backgroundColor: bkgndColor, border: '2px solid #333' }} />
                  </Button>
               </Stack>
               )}
               
               {/* <CopyURLButton /> */}
            </Stack>
         )}

         {testPattern == "customLetterbox" && (
            <Stack
               data-controls
               direction='row'
               spacing={4}
               justifyContent='flex-start'
               alignItems='center'
               sx={{
                  mb: 2,
                  px: 4,
                  py: 2,
                  borderRadius: 1,
                  backgroundColor: "rgba(255,255,255,.7)",
               }}
            >
               <NumberInput
                  label='Width'
                  value={letterboxWidth}
                  callback={setLetterboxWidthWithURL}
                  min={1}
                  max={10000}
               />
               
               <NumberInput
                  label='Height'
                  value={letterboxHeight}
                  callback={setLetterboxHeightWithURL}
                  min={1}
                  max={10000}
               />

               <SelectMenu
                  label='Align Vertical'
                  value={letterboxAlignVertical}
                  callback={setLetterboxAlignVerticalWithURL}
                  options={["top", "center", "bottom"]}
               />

               <SelectMenu
                  label='Align Horizontal'
                  value={letterboxAlignHorizontal}
                  callback={setLetterboxAlignHorizontalWithURL}
                  options={["left", "center", "right"]}
               />
               
               <CopyURLButton />
            </Stack>
         )}

         {testPattern == "movingGradient" && (
            <Stack
               data-controls
               direction='row'
               spacing={4}
               justifyContent='flex-start'
               alignItems='center'
               sx={{
                  mb: 2,
                  px: 4,
                  py: 2,
                  borderRadius: 1,
                  backgroundColor: "rgba(255,255,255,.7)",
               }}
            >
               <Stack direction='row' spacing={2} alignItems='center'>
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Color 1</Typography>
                  <Popover
                     open={gradientColorPicker1Open}
                     anchorEl={gradientColorPicker1Anchor}
                     onClose={() => setGradientColorPicker1Open(false)}
                     anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                     }}
                  >
                     <SketchPicker color={gradientColor1} onChange={handleGradientColor1Change} disableAlpha={true} />
                  </Popover>
                  <Button onClick={(e) => {
                     setGradientColorPicker1Anchor(e.currentTarget);
                     setGradientColorPicker1Open(true);
                  }}>
                     <Box sx={{ width: 40, height: 40, backgroundColor: gradientColor1, border: '2px solid #333', borderRadius: 1 }} />
                  </Button>
                  <TextField
                     value={gradientColor1}
                     onChange={(e) => setGradientColor1WithURL(e.target.value)}
                     size="small"
                     sx={{ width: 100 }}
                     placeholder="#ff0000"
                  />
               </Stack>

               <Stack direction='row' spacing={2} alignItems='center'>
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Color 2</Typography>
                  <Popover
                     open={gradientColorPicker2Open}
                     anchorEl={gradientColorPicker2Anchor}
                     onClose={() => setGradientColorPicker2Open(false)}
                     anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                     }}
                  >
                     <SketchPicker color={gradientColor2} onChange={handleGradientColor2Change} disableAlpha={true} />
                  </Popover>
                  <Button onClick={(e) => {
                     setGradientColorPicker2Anchor(e.currentTarget);
                     setGradientColorPicker2Open(true);
                  }}>
                     <Box sx={{ width: 40, height: 40, backgroundColor: gradientColor2, border: '2px solid #333', borderRadius: 1 }} />
                  </Button>
                  <TextField
                     value={gradientColor2}
                     onChange={(e) => setGradientColor2WithURL(e.target.value)}
                     size="small"
                     sx={{ width: 100 }}
                     placeholder="#0000ff"
                  />
               </Stack>

               <CopyURLButton />
            </Stack>
         )}

         <Box
            data-pattern-container
            sx={{
               width: '100%',
               maxWidth: '100vw',
               aspectRatio: '16/9',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               backgroundColor: '#000',
               margin: '0 auto',
            }}
         >
            <ScaleToWidth 
               contentWidth={canvasDimensions.width} 
               contentHeight={canvasDimensions.height}
               noMaxWidth={testPattern === "grid" || testPattern === "colorBars" || testPattern === "colorBarsPD" || testPattern === "safety"}
            >
               {(parent) => (
                  <Patterns
                     pattern={testPattern}
                     gridSize={gridSize}
                     circles={circles}
                     luma={luma}
                     text={text}
                     grid={grid}
                     bkgndColor={bkgndColor}
                     letterboxWidth={letterboxWidth}
                     letterboxHeight={letterboxHeight}
                     letterboxAlignVertical={letterboxAlignVertical}
                     letterboxAlignHorizontal={letterboxAlignHorizontal}
                     letterboxOutputResolution={letterboxOutputResolution}
                     audioFrequency={audioFrequency}
                     audioVolume={audioVolume}
                     onAudioFrequencyChange={setAudioFrequencyWithURL}
                     onAudioVolumeChange={setAudioVolumeWithURL}
                     gradientColor1={gradientColor1}
                     gradientColor2={gradientColor2}
                     resolution={currentResolution}
                     width={canvasDimensions.width}
                     height={canvasDimensions.height}
                  />
               )}
            </ScaleToWidth>
         </Box>
      </Box>
   );
}
